import api from '@/api/panel-api';
import {Logger} from '@vanti/vue-logger';
import {PanelLights} from './panel-lights';

const log = Logger.get('crestron-panel-lights');

/**
 * @typedef {Object} Rgb
 * @property {number} r
 * @property {number} g
 * @property {number} b
 */

/**
 * Crestron Light Bar integration
 */
export class PanelLightsCrestron extends PanelLights {
  /**
   * @param {VuexStore} store
   */
  constructor(store) {
    super(store);
    this.isLocal = false; // location.hostname === 'localhost';
  }

  /**
   * Set LEDs on room state change
   * @param {RoomState} state
   */
  async onRoomStateChange(state) {
    const color = this.colorForState(state);
    if (!color) {
      log.error(`invalid color for room state: ${state}`);
      return;
    }
    if (this.isLocal) {
      log.debug(`skipping setting color to`, color);
      return;
    }

    log.debug(`setting color to`, color);
    try {
      await this.setLeds(color);
    } catch (e) {
      log.error(e);
    }
  }

  /**
   * check the LED status is correct
   */
  runLedCheck() {
    const state = this._roomState;
    const color = this.colorForState(state);
    if (!color) {
      log.error(`invalid color for room state: ${state}`);
      return;
    }

    this.checkAndSetLed(color).catch(log.error);
  }

  /**
   * Check LED state, and set to color if not set to color already
   * @param {Rgb} color
   */
  async checkAndSetLed(color) {
    try {
      const status = await this.getLeds();
      if (!this.rgbMatch(color, status)) {
        await this.setLeds(color);
      }
    } catch (e) {
      log.error(e);
    }
  }

  /**
   * @return {Promise<Rgb>}
   */
  async getLeds() {
    const status = await this.endpoint.getJson('');
    return status.rgb;
  }

  /**
   * @param {Rgb} color
   * @return {Promise}
   */
  async setLeds(color) {
    await this.endpoint.post('', {rgb: color});
  }

  /**
   * @return {Endpoint}
   */
  get endpoint() {
    const base = this.store.getters['config/panelLightsCrestronEndpoint'];
    const roomName = this.store.getters['config/panelLightsCrestronRoomName'];
    const url = `${base}/light/${roomName}`;
    return new api.Endpoint(url);
  }

  /**
   * @param {Rgb} rgb1
   * @param {Rgb} rgb2
   * @return {boolean}
   */
  rgbMatch(rgb1, rgb2) {
    return (rgb1 && rgb2) && rgb1.r === rgb2.r && rgb1.g === rgb2.g && rgb1.b === rgb2.b;
  }

  /**
   * @param {RoomState} state
   * @return {RGB}
   */
  colorForState(state) {
    const color = Colors[state];
    const brightness = this._brightness;
    if (brightness === 1) {
      return color;
    }
    return {
      r: Math.round(color.r * brightness),
      g: Math.round(color.g * brightness),
      b: Math.round(color.b * brightness)
    };
  }
}

/**
 * @typedef {Object} RGB
 * @property {number} r
 * @property {number} g
 * @property {number} b
 */

/**
 * @type {Object<RoomState, RGB>}
 */
export const Colors = {
  AVAILABLE: {r: 0, g: 255, b: 0},
  OCCUPIED: {r: 255, g: 0, b: 0},
  BOOKED: {r: 255, g: 140, b: 0},
  MAINTENANCE: {r: 128, g: 0, b: 128},
  red: {r: 255, g: 0, b: 0},
  green: {r: 0, g: 255, b: 0},
  off: {r: 0, g: 0, b: 0}
};
